import type { ProblemDetails } from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get reason(): string | undefined {
    return this.problem?.reason;
  }

  get fieldErrors(): Record<string, string[]> | undefined {
    return this.problem?.errors;
  }
}

let antiforgeryToken: string | null = null;

export function resetAntiforgery() {
  antiforgeryToken = null;
}

async function getAntiforgeryToken(): Promise<string> {
  if (antiforgeryToken) {
    return antiforgeryToken;
  }
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' });
  if (!res.ok) {
    throw new ApiError(res.status, null, 'Failed to fetch antiforgery token');
  }
  const body = (await res.json()) as { token: string };
  antiforgeryToken = body.token;
  return body.token;
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    headers['X-Antiforgery-Token'] = await getAntiforgeryToken();
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    signal: opts.signal,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/sign-in')) {
      window.location.assign('/sign-in');
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok) {
    let problem: ProblemDetails | null = null;
    if (contentType.includes('json')) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        problem = null;
      }
    }
    throw new ApiError(res.status, problem, problem?.detail ?? problem?.title ?? `${res.status} ${res.statusText}`);
  }

  if (contentType.includes('json')) {
    return (await res.json()) as T;
  }
  return undefined as T;
}
